import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Boton } from '#components/mobile/ui/Boton';
import { Cargando } from '#components/mobile/ui/Cargando';
import { color, espacio, texto } from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { useLanguage } from '#hooks/useLocale';
import { pedirPuente } from '#uso';

import {
  CabeceraPuente,
  Dato,
  FilaPeticion,
  Inicial,
  ListaRecuentos,
  Seccion,
} from './piezas';
import { PlanPersona } from './PlanPersona';
import {
  diasDesde,
  diasPorSemana,
  fechaCorta,
  haceDias,
  hoyMadrid,
  nombreAparato,
  nombreDe,
  nombreFallo,
  nombrePantalla,
  nombrePlan,
} from './puente';
import type { FichaPuente } from './puente';

const SEMANAS = 8;

/**
 * Ficha de una persona en el Puente de mando: cuándo usó Rumbo, qué usa, qué
 * le falla, lo que ha escrito y cuándo le escribiste. Con su correo completo
 * y «Escribirle un correo» (abre el correo del móvil).
 */
export function FichaPersona({
  id,
  onVolver,
}: {
  id: string;
  onVolver: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const [ficha, setFicha] = useState<FichaPuente | null>(null);
  const [error, setError] = useState(false);
  const [vuelta, setVuelta] = useState(0);
  const [anotado, setAnotado] = useState(false);

  useEffect(() => {
    let vivo = true;
    pedirPuente<FichaPuente>(`/persona/${encodeURIComponent(id)}`)
      .then(f => {
        if (vivo) {
          setFicha(f);
        }
      })
      .catch(() => {
        if (vivo) {
          setError(true);
        }
      });
    return () => {
      vivo = false;
    };
  }, [id, vuelta]);

  const anotarContacto = async () => {
    try {
      await pedirPuente('/contacto', {
        metodo: 'POST',
        cuerpo: { userId: id, nota: null },
      });
      setAnotado(true);
      setVuelta(v => v + 1);
    } catch {
      setAnotado(false);
    }
  };

  const p = ficha?.fila;
  const ahora = Math.floor(Date.now() / 1000);

  return (
    <Page
      padding={0}
      header={
        <CabeceraPuente
          titulo={p ? nombreDe(p) : t('Loading…')}
          subtitulo={
            p
              ? [
                  p.correo,
                  p.alta
                    ? t('joined {{fecha}}', {
                        fecha: fechaCorta(p.alta, locale),
                      })
                    : null,
                  nombrePlan(t, p),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
          onVolver={onVolver}
        />
      }
    >
      <View
        data-testid="puente-ficha"
        style={{
          gap: espacio.tarjetas,
          padding: `${espacio.fila}px ${espacio.margen}px`,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen,
          flexShrink: 0,
        }}
      >
        {error ? (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>
              The server could not be reached. Try again in a while.
            </Trans>
          </Text>
        ) : !ficha || !p ? (
          <Cargando />
        ) : (
          <>
            <View
              style={{
                backgroundColor:
                  p.semaforo === 'rojo'
                    ? color.badSoft
                    : p.semaforo === 'ambar'
                      ? color.warnSoft
                      : color.okSoft,
                borderRadius: 16,
                padding: '12px 14px',
                gap: 8,
              }}
            >
              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
              >
                <Inicial p={p} tamano={44} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ ...texto.titulo, color: color.fg }}>
                    {p.base === 'nunca'
                      ? t('Has never used the app')
                      : p.diasSinUsar === 0
                        ? t('Used the app today')
                        : t('{{count}} days without using the app', {
                            count: p.diasSinUsar ?? 0,
                          })}
                  </Text>
                  <Text
                    style={{
                      ...texto.secundario,
                      color: color.fg2,
                      overflowWrap: 'anywhere',
                      userSelect: 'text',
                    }}
                  >
                    {p.correo}
                  </Text>
                </View>
              </View>
              {p.base === 'solo-abre' && (
                <Text style={{ ...texto.secundario, color: color.fg2 }}>
                  <Trans>
                    Without usage permission: we only know when they open it.
                  </Trans>
                </Text>
              )}
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}>
                  <Boton
                    bloque
                    isDisabled={!p.correo}
                    onPress={() => {
                      window.location.href = `mailto:${p.correo ?? ''}`;
                    }}
                  >
                    <Trans>Write an email</Trans>
                  </Boton>
                </View>
                <View style={{ flex: 1 }}>
                  <Boton
                    bloque
                    variante="fantasma"
                    onPress={() => void anotarContacto()}
                  >
                    {anotado ? t('Noted') : t('Note that I wrote')}
                  </Boton>
                </View>
              </View>
              <Text style={{ ...texto.secundario, color: color.fg2 }}>
                {t('Last time you wrote: {{cuando}}', {
                  cuando: haceDias(
                    t,
                    diasDesde(ficha.contactos[0]?.ts ?? null, ahora),
                  ),
                })}
              </Text>
            </View>

            <Seccion
              titulo={t('Days using Rumbo · last {{count}} weeks', {
                count: SEMANAS,
              })}
            >
              <Semanas dias={ficha.dias} />
            </Seccion>

            <PlanPersona
              p={p}
              locale={locale}
              onCambio={() => setVuelta(v => v + 1)}
            />

            <Seccion titulo={t('What they use (30 days)')}>
              <ListaRecuentos
                filas={ficha.pantallas}
                nombre={c => nombrePantalla(t, c)}
              />
            </Seccion>

            <Seccion titulo={t('Failures (30 days)')}>
              <ListaRecuentos
                filas={ficha.fallos}
                nombre={c => nombreFallo(t, c)}
                vacio={t('No failures. Smooth sailing.')}
                tono="bad"
              />
            </Seccion>

            <Seccion titulo={t('Details')}>
              <Dato
                titulo={t('Last change to their budget')}
                valor={haceDias(t, diasDesde(p.ultimoCambio, ahora))}
              />
              <Dato
                titulo={t('Last time they opened the app')}
                valor={haceDias(t, diasDesde(p.ultimaEntrada, ahora))}
              />
              <Dato
                titulo={t('Days active (30 days)')}
                valor={p.diasActivos30}
              />
              <Dato
                titulo={t('Device and version')}
                valor={
                  [
                    nombreAparato(t, p.aparato),
                    p.version ? `v${p.version}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'
                }
              />
              <Dato
                titulo={t('Usage permission')}
                valor={p.permisoUso ? t('Yes') : t('No')}
                tono={p.permisoUso ? undefined : 'warn'}
              />
              <Dato titulo={t('Budgets')} valor={p.presupuestos} />
              <Dato titulo={t('Shared with')} valor={p.compartidos} />
              <Dato
                titulo={t('Encrypted')}
                valor={p.cifrado ? t('Yes') : t('No')}
                tono={p.cifrado ? undefined : 'bad'}
              />
              <Dato
                titulo={t('Bank connected')}
                valor={p.banco > 0 ? t('Yes') : t('No')}
              />
            </Seccion>

            <Seccion titulo={t('What they wrote')}>
              {ficha.peticiones.length === 0 ? (
                <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
                  <Trans>Nothing yet.</Trans>
                </Text>
              ) : (
                ficha.peticiones.map(pt => (
                  <FilaPeticion
                    key={pt.id}
                    p={pt}
                    onCambio={() => setVuelta(v => v + 1)}
                  />
                ))
              )}
            </Seccion>

            {ficha.contactos.length > 0 && (
              <Seccion titulo={t('When you wrote to them')}>
                {ficha.contactos.map(c => (
                  <Text
                    key={c.ts}
                    style={{ ...texto.secundario, color: color.fg2 }}
                  >
                    {[fechaCorta(c.ts, locale), c.nota]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                ))}
              </Seccion>
            )}
          </>
        )}
      </View>
    </Page>
  );
}

function Semanas({ dias }: { dias: Array<{ dia: string }> }) {
  const { t } = useTranslation();
  const semanas = diasPorSemana(dias, hoyMadrid(), SEMANAS);
  return (
    <View style={{ gap: 4 }}>
      <View
        role="img"
        aria-label={t('Days used per week: {{dias}}', {
          dias: semanas.join(', '),
        })}
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: 8,
          height: 64,
        }}
      >
        {semanas.map((n, i) => (
          <View
            key={i}
            style={{
              flex: 1,
              height: `${Math.max(6, (n / 7) * 100)}%`,
              borderRadius: '4px 4px 0 0',
              backgroundColor:
                n === 0 ? color.surface3 : n >= 3 ? color.accent : color.warn,
            }}
          />
        ))}
      </View>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          ...texto.secundario,
          color: color.fg3,
        }}
      >
        <Text>{t('{{count}} weeks ago', { count: SEMANAS })}</Text>
        <Text>{t('this week')}</Text>
      </View>
    </View>
  );
}
